import type {ILogService} from 'angular';
import {ngAsync} from 'shared/utils/ng-@decorators';
import type {Identified, Loan, Valuable} from 'typings/portaro.be.types';
import type {FactoryFunction, Pageable} from 'typings/portaro.fe.types';
import {states} from '../commons/constants/portaro.constants';
import {replaceAllById} from 'shared/utils/array-utils.fp';

export type GetAllFnType<ITEM extends Identified<any>> = (pageable?: Pageable) => Promise<ITEM[]>;
export type GetSizeFnType = () => Promise<Valuable<number>>;
export type GetOneByIdFnType<ITEM extends Identified<any>> = (id: any) => Promise<ITEM>;

export interface IPageableList<ITEM> {
    state: string;
    size: number | string;
    items: ITEM[];

    loadSize(): Promise<number>;

    loadItems(): Promise<ITEM[]>;

    loadNext(): Promise<ITEM[]>;

    reloadItems(): Promise<ITEM[]>;

    reloadItem(item: ITEM): Promise<ITEM>;

    isLoadedAll(): boolean;
}

export interface PageableListFactory {
    create<ITEM extends Identified<any>>(getAllFn: GetAllFnType<ITEM>, getSizeFn: GetSizeFnType, getOneByIdFn: GetOneByIdFnType<ITEM>): IPageableList<ITEM>;
}

/*@ngInject*/
export function PageableListFactory($log: ILogService) {
    class PageableList<ITEM extends Identified<any>> implements IPageableList<ITEM> {

        public state: string = states.INITIAL;
        public size: number | string = '-';
        public items: ITEM[] = null;

        private static readonly PAGE_SIZE = 20;
        private static readonly FIRST_PAGE = 1;
        private pageToLoad = PageableList.FIRST_PAGE;

        constructor(private getAllFn: GetAllFnType<ITEM>, private getSizeFn: GetSizeFnType, private getOneByIdFn: GetOneByIdFnType<ITEM>) {
        }

        @ngAsync()
        public async loadSize(): Promise<number> {
           try {
                const size = await this.getSizeFn();
                const value = size.value;
                this.size = value;
                return value;
            } catch (e) {
                $log.error('PageableList: Error at loading number of items: ', e);
                throw e;
            }
        }

        @ngAsync()
        public async loadItems(): Promise<ITEM[]> {
            if (!this.items) {
                this.state = states.PROCESSING;

                try {
                    const page = this.getRequestPage();
                    const items = await this.getAllFn(page);
                    this.items = items;
                    this.pageToLoad = page.pageNumber + 1;
                    this.state = states.NORMAL;
                    return items;
                } catch (e) {
                    $log.error('PageableList: Error loading items: ', e);
                    throw e;
                }
            }
        }

        @ngAsync()
        public async loadNext(): Promise<ITEM[]> {
            if (!this.isLoadedAll()) {
                this.state = states.PROCESSING;

                try {
                    const page = this.getRequestPage();
                    const items = await this.getAllFn(page);
                    this.items = this.items.concat(items);
                    this.pageToLoad = page.pageNumber + 1;
                    this.state = states.NORMAL;
                } catch (e) {
                    $log.error('PageableList: Error loading next page: ', e);
                    throw e;
                }
            }

            return this.items;
        }

        @ngAsync()
        public async reloadItems(): Promise<ITEM[]> {
            this.loadSize();
            if (this.items) {
                this.items = null;
            }
            this.pageToLoad = PageableList.FIRST_PAGE;
            return this.loadItems();
        }

        @ngAsync()
        public async reloadItem(item: ITEM): Promise<ITEM> {
            try {
                const reloadedItem: ITEM = await this.getOneByIdFn(item.id);
                replaceAllById(reloadedItem)(this.items);
                return reloadedItem;
            } catch (e) {
                $log.error('PageableList: Error in reloading item: ', e);
                throw e;
            }
        }

        public showRenewalColumn() {
            function hasItemEnabledRenewability(oneItem: any): oneItem is Loan {
                return oneItem.renewability.enabled;
            }
            return (this.items || []).some((oneItem) => hasItemEnabledRenewability(oneItem));
        }

        public showReturnLoan() {
            return true;
        }

        public enableRenewAllButton() {
            if (!this.items) {
                return false;
            }
            if (!this.isLoadedAll()) { // there can be renewable items on next pages
                return true;
            }
            function isItemRenewable(oneItem: any): oneItem is Loan {
                return oneItem.renewability.renewable;
            }
            return (this.items || []).some((oneItem) => isItemRenewable(oneItem));
        }

        public enableReturnButton() {
            return this.items && this.items.length > 0;
        }

        public isLoadedAll(): boolean {
            if (this.items) {
                return this.items.length >= (this.size as number); // fixme: fix that size is number | string
            } else {
                return this.size === 0;
            }
        }

        private getRequestPage(): Pageable {
            return {pageNumber: this.pageToLoad, pageSize: PageableList.PAGE_SIZE};
        }
    }

    this.create = <ITEM extends Identified<any>>(getAllFn: GetAllFnType<ITEM>, getSizeFn: GetSizeFnType, getOneByIdFn: GetOneByIdFnType<ITEM>) => {
        return new PageableList<ITEM>(getAllFn, getSizeFn, getOneByIdFn);
    };
}

(PageableListFactory as FactoryFunction).factoryName = 'pageableListFactory';
